import React from 'react';
import {TooltipRenderProps} from 'react-joyride';
import './styles.css';
import {tourTooltipText} from '../../texts/tourTooltipText';
import {TourStepData} from '../../utils/buildSteps';

const TourTooltip: React.FC<TooltipRenderProps> = ({
  index,
  size,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
  isLastStep,
  continuous,
}) => {
  const data = (step.data ?? {}) as TourStepData;
  const showBack = index > 0;
  const primaryLabel =
    isLastStep || !continuous ? tourTooltipText.done : tourTooltipText.next;

  return (
    <div {...tooltipProps} className="tourTooltip">
      <header className="tourTooltipHeader">
        <span className="tourTooltipBadge">
          <span className="tourTooltipBadgeDot" aria-hidden="true" />
          {tourTooltipText.badge}
        </span>
        <button
          {...closeProps}
          type="button"
          className="tourTooltipClose"
          aria-label={tourTooltipText.closeAriaLabel}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </header>

      {data.image && (
        <div className="tourTooltipMedia">
          <img
            src={data.image}
            alt={data.title ?? ''}
            className="tourTooltipImage"
          />
        </div>
      )}

      {data.title && <h4 className="tourTooltipTitle">{data.title}</h4>}
      {data.description && (
        <p className="tourTooltipDescription">{data.description}</p>
      )}
      {data.link && (
        <a
          href={data.link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="tourTooltipLink">
          {data.link.label}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false">
            <path d="M7 17 17 7" />
            <path d="M7 7h10v10" />
          </svg>
        </a>
      )}

      <footer className="tourTooltipFooter">
        <div
          className="tourTooltipDots"
          role="tablist"
          aria-label={`${index + 1} / ${size}`}>
          {Array.from({length: size}).map((_, i) => (
            <span
              key={i}
              className={`tourTooltipDot${
                i === index ? ' tourTooltipDotActive' : ''
              }`}
              aria-current={i === index ? 'step' : undefined}
            />
          ))}
        </div>
        <div className="tourTooltipActions">
          <button {...skipProps} type="button" className="tourTooltipSkip">
            {tourTooltipText.skip}
          </button>
          {showBack && (
            <button {...backProps} type="button" className="tourTooltipBack">
              {tourTooltipText.back}
            </button>
          )}
          <button
            {...primaryProps}
            type="button"
            className="tourTooltipPrimary">
            {primaryLabel}
            {!isLastStep && continuous && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false">
                <path d="m9 18 6-6-6-6" />
              </svg>
            )}
          </button>
        </div>
      </footer>
    </div>
  );
};

export default TourTooltip;
